import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  alpha,
  useTheme,
  LinearProgress,
  Chip,
  Avatar,
} from '@mui/material';
import {
  CheckCircle,
  Rocket,
  Dashboard,
  Notifications,
  School,
  VideoLibrary,
  Description,
  Support,
  TrendingUp,
} from '@mui/icons-material';

interface FinalSetupStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const FinalSetupStep: React.FC<FinalSetupStepProps> = ({ onNext, data }) => {
  const theme = useTheme();
  const [isLaunching, setIsLaunching] = useState(false);

  const completedSteps = [
    { label: 'Company Profile', completed: !!data.company },
    { label: 'Supply Chain Mapping', completed: !!data['supply-chain'] },
    { label: 'Data Integration', completed: true }, // Optional
    { label: 'HITL Understanding', completed: !!data.hitl },
    { label: 'Agent Configuration', completed: !!data.agents },
  ];

  const completionPercentage =
    (completedSteps.filter((s) => s.completed).length / completedSteps.length) * 100;

  const nextSteps = [
    {
      icon: <Dashboard />,
      title: 'Explore Your Dashboard',
      description: 'View real-time insights and AI recommendations',
      action: 'Go to Dashboard',
    },
    {
      icon: <Notifications />,
      title: 'Review Pending Tasks',
      description: 'Check any AI recommendations waiting for your approval',
      action: 'View Tasks',
    },
    {
      icon: <TrendingUp />,
      title: 'Run Your First Analysis',
      description: 'Let AI analyze your supply chain and generate insights',
      action: 'Start Analysis',
    },
  ];

  const resources = [
    {
      icon: <School />,
      title: 'Quick Start Guide',
      description: '5-minute tutorial on using ASCIN',
      link: '/help/quick-start',
    },
    {
      icon: <VideoLibrary />,
      title: 'Video Tutorials',
      description: 'Watch how to use key features',
      link: '/help/videos',
    },
    {
      icon: <Description />,
      title: 'Documentation',
      description: 'Detailed guides and API reference',
      link: '/help/docs',
    },
    {
      icon: <Support />,
      title: '24/7 Support',
      description: 'Get help from our team anytime',
      link: '/support',
    },
  ];

  const handleLaunch = async () => {
    setIsLaunching(true);
    // Simulate setup completion
    await new Promise((resolve) => setTimeout(resolve, 2000));
    onNext({ setupComplete: true, launchTime: new Date().toISOString() });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <Rocket sx={{ fontSize: 40 }} />
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          You're All Set!
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Your ASCIN system is ready to launch
        </Typography>
      </Box>

      {/* Progress Summary */}
      <Card sx={{ mb: 4, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
              Setup Progress
            </Typography>
            <Chip
              label={`${Math.round(completionPercentage)}% Complete`}
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 2,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: theme.palette.success.main,
              },
            }}
          />
          <List dense>
            {completedSteps.map((step, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircle
                    sx={{
                      color: step.completed
                        ? theme.palette.success.main
                        : theme.palette.grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  primaryTypographyProps={{
                    sx: {
                      textDecoration: step.completed ? 'none' : 'line-through',
                      color: step.completed ? 'text.primary' : 'text.secondary',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* What's Configured */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        What's Been Configured
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Company Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.company?.companyName || 'Not set'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {data.company?.industry || ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Supply Chain
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data['supply-chain']?.suppliers?.length || 0} Suppliers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data['supply-chain']?.products?.length || 0} Products
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data['supply-chain']?.locations?.length || 0} Locations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                AI Agents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Object.values(data.agents || {}).filter((a: any) => a.enabled).length} agents
                enabled
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready to provide insights
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Integrations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.data?.integrations?.length || 0} data sources
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {data.data?.integrations?.length > 0 ? 'Connected' : 'Manual upload ready'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Next Steps */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        What to Do Next
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {nextSteps.map((step, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  {step.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {step.description}
                </Typography>
                <Button size="small" variant="outlined">
                  {step.action}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Resources */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Helpful Resources
          </Typography>
          <Grid container spacing={2}>
            {resources.map((resource, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      mr: 2,
                    }}
                  >
                    {resource.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {resource.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {resource.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Launch Button */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Rocket sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Ready to Transform Your Supply Chain?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Click below to launch your ASCIN dashboard and start getting AI-powered insights
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleLaunch}
            disabled={isLaunching}
            sx={{
              bgcolor: 'white',
              color: theme.palette.primary.main,
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.9),
              },
              '&:disabled': {
                bgcolor: alpha('#ffffff', 0.5),
              },
            }}
          >
            {isLaunching ? 'Launching...' : 'Launch ASCIN Dashboard'}
          </Button>
          {isLaunching && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress sx={{ bgcolor: alpha('#ffffff', 0.3) }} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Setting up your workspace...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Final Note */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          🎉 <strong>Congratulations!</strong> You've completed the setup. Your AI agents are
          ready to start analyzing your supply chain. Remember, you're always in control - review
          and approve all recommendations before they're executed.
        </Typography>
      </Alert>
    </Box>
  );
};

export default FinalSetupStep;
